import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { 
  PlusIcon, 
  FunnelIcon, 
  MagnifyingGlassIcon,
  PlayIcon,
  PauseIcon,
  CheckIcon,
  ClockIcon,
  TruckIcon
} from '@heroicons/react/24/outline';
import { formatDistanceToNow, getPriorityColor, getStatusColor, formatDuration, cn } from '../utils';
import apiService from '../services/api';
import toast from 'react-hot-toast';
import TaskAssignment from '../components/TaskAssignment';

const Tasks = () => {
  const { isAdmin } = useAuth();
  const { t } = useTranslation();
  const [tasks, setTasks] = useState([]);
  const [mechanics, setMechanics] = useState([]);
  const [cars, setCars] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({});
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    sortBy: 'createdAt',
    sortOrder: 'desc'
  });
  const [totalPages, setTotalPages] = useState(1);
  const [showFilters, setShowFilters] = useState(false);
  const [selectedTasks, setSelectedTasks] = useState([]);
  const [showTaskAssignment, setShowTaskAssignment] = useState(false);

  useEffect(() => {
    fetchTasks();
    if (isAdmin) {
      fetchMechanics();
      fetchCars();
    }
  }, [filters, pagination]);

  const fetchTasks = async () => {
    setLoading(true);
    try {
      const response = await apiService.getTasks({ ...filters, ...pagination });
      if (response.success && response.tasks) {
        setTasks(response.tasks);
        setTotalPages(response.pagination?.pages || 1);
      }
    } catch (error) {
      toast.error(error.message || 'Failed to fetch tasks');
    } finally {
      setLoading(false);
    }
  };

  const fetchMechanics = async () => {
    try {
      const response = await apiService.getMechanics();
      if (response.success && response.mechanics) {
        setMechanics(response.mechanics);
      }
    } catch (error) {
      console.error('Failed to fetch mechanics:', error);
    }
  };

  const fetchCars = async () => {
    try {
      const response = await apiService.getCars({ limit: 100 });
      if (response.success && response.cars) {
        setCars(response.cars);
      }
    } catch (error) {
      console.error('Failed to fetch cars:', error);
    }
  };

  const handleTaskAction = async (taskId, action) => {
    try {
      let response;
      switch (action) {
        case 'start':
          response = await apiService.startTask(taskId);
          break;
        case 'pause':
          response = await apiService.pauseTask(taskId);
          break;
        case 'complete':
          response = await apiService.completeTask(taskId);
          break;
      }
      
      if (response.success) {
        toast.success(`Task ${action}ed successfully`);
        fetchTasks();
      }
    } catch (error) {
      toast.error(error.message || `Failed to ${action} task`);
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: value || undefined
    }));
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const handleSearch = (search) => {
    setFilters(prev => ({
      ...prev,
      search: search || undefined
    }));
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const clearFilters = () => {
    setFilters({});
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const handleSelectTask = (taskId) => {
    setSelectedTasks(prev => 
      prev.includes(taskId) 
        ? prev.filter(id => id !== taskId)
        : [...prev, taskId]
    );
  };

  const handleSelectAll = () => {
    if (selectedTasks.length === tasks.length) {
      setSelectedTasks([]);
    } else {
      setSelectedTasks(tasks.map(task => task._id));
    }
  };

  const getTaskIcon = (task) => {
    if (task.status === 'completed') {
      return <CheckIcon className="h-4 w-4 text-green-500" />;
    } else if (task.status === 'in-progress') {
      return <PauseIcon className="h-4 w-4 text-yellow-500" />;
    } else {
      return <PlayIcon className="h-4 w-4 text-blue-500" />;
    }
  };

  const getTaskAction = (task) => {
    if (task.status === 'completed') {
      return null;
    } else if (task.status === 'in-progress') {
      return (
        <div className="flex space-x-2">
          <button
            onClick={() => handleTaskAction(task._id, 'pause')}
            className="p-1 text-yellow-500 hover:text-yellow-400"
            title={t('tasks.actions.pauseTask')}
          >
            <PauseIcon className="h-4 w-4" />
          </button>
          <button
            onClick={() => handleTaskAction(task._id, 'complete')}
            className="p-1 text-green-500 hover:text-green-400"
            title={t('tasks.actions.completeTask')}
          >
            <CheckIcon className="h-4 w-4" />
          </button>
        </div>
      );
    } else {
      return (
        <button
          onClick={() => handleTaskAction(task._id, 'start')}
          className="p-1 text-blue-500 hover:text-blue-400"
          title={t('tasks.actions.startTask')}
        >
          <PlayIcon className="h-4 w-4" />
        </button>
      );
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold text-white">{t('tasks.title')}</h1>
        {isAdmin && (
          <button
            onClick={() => setShowTaskAssignment(true)}
            className="btn btn-primary"
          >
            <PlusIcon className="h-4 w-4 mr-2" />
            {t('tasks.newTask')}
          </button>
        )}
      </div>

      {/* Filters */}
      <div className="bg-dark-800 border border-dark-700 rounded-lg p-4">
        <div className="flex items-center space-x-4">
          <div className="flex-1">
            <div className="relative">
              <MagnifyingGlassIcon className="h-5 w-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-dark-400" />
              <input
                type="text"
                placeholder={t('tasks.searchPlaceholder')}
                className="form-input pl-10"
                value={filters.search || ''}
                onChange={(e) => handleSearch(e.target.value)}
              />
            </div>
          </div>
          <button
            onClick={() => setShowFilters(!showFilters)}
            className="btn btn-secondary"
          >
            <FunnelIcon className="h-4 w-4 mr-2" />
            Filters
          </button>
        </div>

        {showFilters && (
          <div className="mt-4 grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <label className="form-label">{t('tasks.filters.status')}</label>
              <select
                className="form-input"
                value={filters.status || ''}
                onChange={(e) => handleFilterChange('status', e.target.value)}
              >
                <option value="">{t('tasks.filters.allStatus')}</option>
                <option value="pending">{t('tasks.status.pending')}</option>
                <option value="in-progress">{t('tasks.status.inProgress')}</option>
                <option value="completed">{t('tasks.status.completed')}</option>
                <option value="cancelled">{t('tasks.status.cancelled')}</option>
              </select>
            </div>
            <div>
              <label className="form-label">{t('tasks.filters.priority')}</label>
              <select
                className="form-input"
                value={filters.priority || ''}
                onChange={(e) => handleFilterChange('priority', e.target.value)}
              >
                <option value="">{t('tasks.filters.allPriorities')}</option>
                <option value="high">{t('tasks.priority.high')}</option>
                <option value="medium">{t('tasks.priority.medium')}</option>
                <option value="low">{t('tasks.priority.low')}</option>
              </select>
            </div>
            {isAdmin && (
              <div>
                <label className="form-label">{t('tasks.filters.mechanic')}</label>
                <select
                  className="form-input"
                  value={filters.assignedTo || ''}
                  onChange={(e) => handleFilterChange('assignedTo', e.target.value)}
                >
                  <option value="">{t('tasks.filters.allMechanics')}</option>
                  {mechanics.map(mechanic => (
                    <option key={mechanic._id} value={mechanic._id}>
                      {mechanic.name}
                    </option>
                  ))}
                </select>
              </div>
            )}
            <div>
              <label className="form-label">{t('tasks.filters.car')}</label>
              <select
                className="form-input"
                value={filters.car || ''}
                onChange={(e) => handleFilterChange('car', e.target.value)}
              >
                <option value="">{t('tasks.filters.allCars')}</option>
                {cars.map(car => (
                  <option key={car._id} value={car._id}>
                    {car.numberPlate} - {car.make} {car.model}
                  </option>
                ))}
              </select>
            </div>
          </div>
        )}

        {(Object.keys(filters).length > 0) && (
          <div className="mt-4 flex justify-between items-center">
            <span className="text-sm text-dark-300">
              {Object.keys(filters).length} filter(s) applied
            </span>
            <button
              onClick={clearFilters}
              className="text-sm text-primary-500 hover:text-primary-400"
            >
              Clear all filters
            </button>
          </div>
        )}
      </div>

      {/* Tasks Table */}
      <div className="bg-dark-800 border border-dark-700 rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-dark-700">
            <thead className="bg-dark-700">
              <tr>
                <th className="table-header w-4">
                  <input
                    type="checkbox"
                    checked={selectedTasks.length === tasks.length && tasks.length > 0}
                    onChange={handleSelectAll}
                    className="rounded border-dark-600 text-primary-600 focus:ring-primary-500"
                  />
                </th>
                <th className="table-header">{t('tasks.table.task')}</th>
                <th className="table-header">{t('tasks.table.status')}</th>
                <th className="table-header">{t('tasks.table.priority')}</th>
                <th className="table-header">{t('tasks.table.assignedTo')}</th>
                <th className="table-header">{t('tasks.table.car')}</th>
                <th className="table-header">{t('tasks.table.created')}</th>
                <th className="table-header">{t('tasks.table.actions')}</th>
              </tr>
            </thead>
            <tbody className="bg-dark-800 divide-y divide-dark-700">
              {loading ? (
                <tr>
                  <td colSpan={8} className="table-cell text-center py-12">
                    <div className="spinner mx-auto"></div>
                  </td>
                </tr>
              ) : tasks.length === 0 ? (
                <tr>
                  <td colSpan={8} className="table-cell text-center py-12">
                    <ClockIcon className="h-12 w-12 text-dark-400 mx-auto mb-4" />
                    <p className="text-dark-300">{t('tasks.noTasksFound')}</p>
                  </td>
                </tr>
              ) : (
                tasks.map((task) => (
                  <tr key={task._id} className="hover:bg-dark-700">
                    <td className="table-cell">
                      <input
                        type="checkbox"
                        checked={selectedTasks.includes(task._id)}
                        onChange={() => handleSelectTask(task._id)}
                        className="rounded border-dark-600 text-primary-600 focus:ring-primary-500"
                      />
                    </td>
                    <td className="table-cell">
                      <div className="flex items-center">
                        {getTaskIcon(task)}
                        <div className="ml-3">
                          <Link
                            to={`/tasks/${task._id}`}
                            className="text-white hover:text-primary-400 font-medium"
                          >
                            {task.title}
                          </Link>
                          {task.description && (
                            <p className="text-sm text-dark-400 truncate max-w-xs">
                              {task.description}
                            </p>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="table-cell">
                      <span className={cn('badge', getStatusColor(task.status))}>
                        {task.status}
                      </span>
                    </td>
                    <td className="table-cell">
                      <span className={cn('badge', getPriorityColor(task.priority))}>
                        {task.priority}
                      </span>
                    </td>
                    <td className="table-cell">
                      <div className="flex items-center">
                        <div className="flex-shrink-0 h-8 w-8">
                          <div className="h-8 w-8 rounded-full bg-primary-600 flex items-center justify-center">
                            <span className="text-xs font-medium text-white">
                              {task.assignedTo.name.charAt(0)}
                            </span>
                          </div>
                        </div>
                        <div className="ml-3">
                          <p className="text-sm font-medium text-white">
                            {task.assignedTo.name}
                          </p>
                        </div>
                      </div>
                    </td>
                    <td className="table-cell">
                      {task.car ? (
                        <div className="flex items-center">
                          <TruckIcon className="h-4 w-4 text-dark-400 mr-2" />
                          <Link
                            to={`/cars/${task.car._id}`}
                            className="text-primary-400 hover:text-primary-300"
                          >
                            {task.car.numberPlate}
                          </Link>
                        </div>
                      ) : (
                        <span className="text-dark-400">{t('tasks.noCar')}</span>
                      )}
                    </td>
                    <td className="table-cell">
                      <span className="text-sm text-dark-300">
                        {formatDistanceToNow(task.createdAt)}
                      </span>
                    </td>
                    <td className="table-cell">
                      <div className="flex items-center space-x-2">
                        {getTaskAction(task)}
                        <Link
                          to={`/tasks/${task._id}`}
                          className="text-primary-500 hover:text-primary-400"
                        >
                          View
                        </Link>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-dark-300">
            Showing page {pagination.page} of {totalPages}
          </p>
          <div className="flex space-x-2">
            <button
              onClick={() => setPagination(prev => ({ ...prev, page: Math.max(1, prev.page - 1) }))}
              disabled={pagination.page === 1}
              className="btn btn-secondary disabled:opacity-50"
            >
              Previous
            </button>
            <button
              onClick={() => setPagination(prev => ({ ...prev, page: Math.min(totalPages, prev.page + 1) }))}
              disabled={pagination.page === totalPages}
              className="btn btn-secondary disabled:opacity-50"
            >
              Next
            </button>
          </div>
        </div>
      )}

      {/* Task Assignment Modal */}
      <TaskAssignment
        isOpen={showTaskAssignment}
        onClose={() => setShowTaskAssignment(false)}
        onTaskCreated={(newTask) => {
          setTasks(prev => [newTask, ...prev]);
          fetchTasks(); // Refresh the full list
        }}
      />
    </div>
  );
};

export default Tasks;